import { Body, Box, Button, Heading, Spacer, VStack } from '@hover/blueprint';
import React, { useContext } from 'react';
import { useHistory } from 'react-router';
import type { InferType } from 'yup';

import { OnboardingContext } from 'src/modules/onboarding/components/OnboardingContext';
import { useOnboardingError } from 'src/modules/onboarding/hooks/useOnboardingError';
import { makeEmailValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { Form as SharedForm, TextField } from 'src/shared/components/forms';
import { useSendResetPasswordInstructionsMutation } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useTranslate,
} from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed, pageViewed } from 'src/shared/telemetry';

export type EmailFormData = InferType<
  ReturnType<typeof makeEmailValidationSchema>
>;

export const ResetPassword = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const history = useHistory();
  const { logError } = useOnboardingError();
  const [sendResetInstructions] = useSendResetPasswordInstructionsMutation();

  const [_state, dispatch] = useContext(OnboardingContext);

  const validationSchema = makeEmailValidationSchema(t);
  const initialValues = { email: _state?.email ?? '' };

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Forgot-password_input',
      feature: 'onboarding',
    });
  });

  const onSubmit = async (formData: EmailFormData) => {
    buttonPressed({
      button_text: 'Next',
      page_or_screen_name: 'Forgot-password_input',
      feature: 'onboarding',
    });
    const { email } = formData;
    if (!email) return;

    dispatch({ email });

    try {
      await sendResetInstructions({
        variables: {
          email,
        },
      });
    } catch (e) {
      logError(e as Error);
    } finally {
      history.push(`${Routes.SIGN_UP_ONBOARDING.path}/check`);
    }
  };

  return (
    <Box alignItems="center" height="100vh" justifyContent="center" m={500}>
      <VStack gap={100}>
        <Heading
          color="neutral700"
          fontWeight="headingLight"
          size={{ base: 700, tablet: 900 }}
        >
          {t('onboarding.forgotPassword')}
        </Heading>
        <Body color="neutral.500" paddingBottom={200}>
          {t('onboarding.enterYourEmailAnd')}
        </Body>
        <Spacer />
        <VStack marginTop={200} width={1}>
          <SharedForm<EmailFormData>
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            width={{ base: 1, tablet: 'auto' }}
          >
            <TextField
              minWidth={{ tablet: 750 }}
              name="email"
              placeholder={t('onboarding.email')}
              type="email"
            />
            <Button marginTop={300} type="submit" width={1}>
              {t('onboarding.next')}
            </Button>
          </SharedForm>
        </VStack>
      </VStack>
    </Box>
  );
};
